import { CitySelectorText } from '@components/CitySelector/CitySelectorText'
import { CitySelectorAction, CitySelectorState, createToken, reducer } from '@components/CitySelector/stateManagement'
import { useLocationSuggestion } from '@data/hooks/useLocationSuggestion'
import { useLocationSuggestionPlace } from '@data/hooks/useLocationSuggestionPlace'
import {
    Autocomplete,
    AutocompleteInputChangeReason,
    AutocompleteProps,
    AutocompleteRenderInputParams,
    SxProps,
    TextFieldProps,
} from '@internations/ds'
import { noop } from 'lodash-es'
import { Dispatch, Fragment, ReactElement, SyntheticEvent, useEffect, useReducer } from 'react'
import { useDebounce } from 'rooks'

type InitialValue = TPlace | TLocation

type AllowedTextFieldProps = Pick<TextFieldProps, 'label' | 'onFocus' | 'onBlur' | 'placeholder'>

type AllowedAutocompleteProps = Omit<
    AutocompleteProps<TPlaceSuggestion, false, false, true>,
    | 'onChange'
    | 'onInputChange'
    | 'options'
    | 'filterOptions'
    | 'value'
    | 'getOptionLabel'
    | 'renderOption'
    | 'isOptionEqualToValue'
    | 'limitTags'
    | 'error'
    | 'autoFocus'
    | 'className'
    | 'sx'
>

export type CitySelectorProps = {
    /**
     * Prepopulates the field.
     */
    initialValue?: InitialValue | null

    /**
     * Called when the user selects a city and the place object is fulled resolved.
     */
    onSelect?: (city: TPlace | undefined) => void

    /**
     * Called for each `change` event of the input field
     */
    onInputChange?: (typedValue: string) => void

    /**
     * Called when the user clears the input field.
     */
    onClear?: () => void

    label?: string
    error?: string
    onLoading?: (isLoading: boolean) => void
    shouldClear?: boolean
    className?: string
    sx?: SxProps
} & AllowedTextFieldProps &
    AllowedAutocompleteProps

type DispatchProps = { dispatch: Dispatch<CitySelectorAction> }

const placeNameLabel = ({ label, description }: TPlaceSuggestion): string =>
    `${label}${description ? `, ${description}` : ''}`

function resolveInitialPlaceId(initialValue?: InitialValue | null): string {
    return initialValue && 'placeId' in initialValue ? (initialValue?.placeId ?? '') : ''
}

function getInitialState(initialValue?: InitialValue | null): CitySelectorState {
    const preselectedCity: TPlaceSuggestion | null =
        initialValue && initialValue.city
            ? {
                  placeId: resolveInitialPlaceId(initialValue),
                  label: initialValue.city,
                  description: initialValue.country.name,
              }
            : null
    const initialValues: CitySelectorState = {
        locationSuggestions: [],
        selectedLocationSuggestion: preselectedCity,
        typedText: preselectedCity ? `${preselectedCity.label}, ${preselectedCity.description ?? ''}` : '',
        sessionToken: createToken(),
        enableLocationSuggestions: Boolean(preselectedCity?.label.length),
        selectedPlaceId: resolveInitialPlaceId(initialValue),
        selectedIocCode: initialValue?.country.iocCode ?? null,
    }

    return initialValues
}

/**
 * Uses the user-chosen placeId to fetch the full TPlace object and call `onSelect()` with it.
 */
const useUpdateSelectedPlace = ({
    state,
    dispatch,
    placeId,
    onSelect,
}: Required<Pick<CitySelectorProps, 'onSelect'>> &
    DispatchProps & {
        state: CitySelectorState
        placeId?: TPlace['placeId']
    }): TIocCode | undefined => {
    const selectedPlace = useLocationSuggestionPlace(placeId)

    useEffect(() => {
        if (selectedPlace.isSuccess && state.selectedPlaceId !== selectedPlace.data.placeId) {
            dispatch({ type: 'SET_SELECTED_PLACEID', payload: selectedPlace.data })
            onSelect(selectedPlace.data)
        }
    }, [dispatch, onSelect, selectedPlace.data, selectedPlace.isSuccess, state.selectedPlaceId])

    return selectedPlace.data?.country.iocCode
}

/**
 * Fetches suggested places that match the current user entry.
 */
function useLocationSuggestions(
    state: CitySelectorState,
    dispatch: DispatchProps['dispatch'],
    onLoading: Required<Pick<CitySelectorProps, 'onLoading'>>['onLoading']
): boolean {
    const locationSuggestions = useLocationSuggestion(
        { query: state.typedText, sessionToken: state.sessionToken },
        { enabled: state.enableLocationSuggestions }
    )
    const locationSuggestionIds = locationSuggestions.data.map((ls) => ls.placeId).join(',')
    useEffect(
        () =>
            dispatch({
                type: 'SET_LOCATION_SUGGESTIONS',
                payload: locationSuggestions.data,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            locationSuggestionIds,

            // We are knowingly omitting these dependencies:
            //     locationSuggestions.data — instead we use a primitive value with enough coverage to detect meaningful changes to the array
            //     dispatch — the linter can't tell that this is the same stable React function that's returned by useReducer
            // If you edit this useEffect, please check for other dependencies that are not listed above.
        ]
    )

    useEffect(() => {
        onLoading(locationSuggestions.isFetching)
    }, [locationSuggestions.isFetching, onLoading])

    return locationSuggestions.isFetching
}

/**
 * Manages some of the event callbacks that are related to the field.
 */
function useOnInputChange({
    dispatch,
    onSelect,
    onInputChange,
    onClear,
}: Required<Pick<CitySelectorProps, 'onSelect' | 'onInputChange' | 'onClear'>> & DispatchProps): (
    _event: SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason
) => void {
    const setTypedText = useDebounce((payload: string) => dispatch({ type: 'SET_TYPED_TEXT', payload }), 300)

    return function onInputChangeHandler(_event, value, reason) {
        if (reason === 'input') {
            onInputChange(value)
        }

        if (reason !== 'clear' && value) {
            setTypedText(value)

            return
        }

        dispatch({ type: 'CLEAR_STATE' })
        onClear()
        onSelect(undefined)
    }
}

function useClearEntry({ dispatch, shouldClear }: Pick<CitySelectorProps, 'shouldClear'> & DispatchProps): void {
    useEffect(
        () => {
            if (shouldClear) {
                dispatch({ type: 'CLEAR_STATE' })
            }
        },
        // We only want to run this effect when `shouldClear` changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [shouldClear]
    )
}

const filterOptions = (options: TPlaceSuggestion[]) => options
const renderOption = (place: TPlaceSuggestion) => <Fragment key={place.placeId}>{placeNameLabel(place)}</Fragment>
const getOptionLabel = (place: TPlaceSuggestion) => (place.label ? placeNameLabel(place) : '')
const isOptionEqualToValue = (option: TPlaceSuggestion, value: TPlaceSuggestion) => option.placeId === value.placeId

/**
 * A form field that searches for places.
 *
 * When the user types in a query, a list of suggestions is displayed. A place is selected only after the user chooses one of the suggestions. `onSelect` will be called when the place is chosen. Until then, the caller should assume the user has not chosen any place other than the initial value.
 *
 * If using Formik, use `<FormikCitySelector>` instead.
 */
export function CitySelector({
    initialValue,
    onSelect = noop,
    onInputChange = noop,
    onClear = noop,
    label = 'City',
    error,
    onFocus,
    onBlur,
    id = 'citySelector',
    onLoading = noop,
    shouldClear,
    placeholder,
    ...rest
}: CitySelectorProps): ReactElement {
    const [state, dispatch] = useReducer(reducer, getInitialState(initialValue))
    const onUserEntry = useOnInputChange({ dispatch, onSelect, onInputChange, onClear })
    useClearEntry({ dispatch, shouldClear })
    const isFetchingSuggestions = useLocationSuggestions(state, dispatch, onLoading)
    useUpdateSelectedPlace({
        state,
        dispatch,
        onSelect,
        placeId: state.selectedLocationSuggestion?.placeId,
    })
    const renderInput = (params: AutocompleteRenderInputParams) => (
        <CitySelectorText
            onFocus={onFocus}
            onBlur={onBlur}
            iocCode={state.selectedIocCode}
            label={label}
            error={error}
            textFieldProps={params}
            placeholder={placeholder}
        />
    )
    const onSelectionChange = (place: TPlaceSuggestion | string | null) => {
        if (!place || typeof place === 'string') {
            return
        }

        dispatch({
            type: 'SET_SELECTED_LOCATION_SUGGESTION',
            payload: place.placeId,
        })
    }

    return (
        <Autocomplete<{
            valueType: TPlaceSuggestion
            multiValued: false
            valueRequired: false
            freeInputAllowed: true
        }>
            id={id}
            hideEmptyPopper
            forcePopupIcon={false}
            renderInput={renderInput}
            onChange={onSelectionChange}
            onInputChange={onUserEntry}
            options={state.locationSuggestions}
            filterOptions={filterOptions}
            loading={isFetchingSuggestions}
            value={state.selectedLocationSuggestion}
            getOptionLabel={getOptionLabel}
            renderOption={renderOption}
            isOptionEqualToValue={isOptionEqualToValue}
            {...rest}
        />
    )
}
